import { FeConfigType, FeResourceType, FeByType } from "config/config";
import { getBundlesById } from "containers/Bundles/Bundles.selectors";
import { executionFilterPerLocationLink } from "containers/ExecutionFilter/utils";
import { getProjectsById } from "containers/Projects/Projects.selectors";
import { getWorkspaceData } from "containers/Workspaces/Workspaces.selectors";
import qs from "qs";
import { useLocation, useRouteMatch } from "react-router";
import { useSelector } from "react-redux";
import {
    advancedSearchPathFragment,
    bundleManagerPathFragment,
    executionsPathFragment,
    logSubPageKey,
    settingsPathFragment,
    storageAnalysisSubPageKey,
    trashPath,
    usersRolesPathFragment,
} from "utils/RouterManager/constants";
import {
    CanvasType,
    getCanvasLink,
    getRelativeUrl,
    getWorkspaceLink,
    getWorkspaceUrl,
} from "utils/service";

export const useMatchExecutionFilterLocation = () => {
    const location = useLocation();
    const queryParams = qs.parse(location.search.substring(1));
    const pathMatch = useRouteMatch<{ recordTypeName: FeResourceType }>("/:recordTypeName");

    const isExecutionFilterPage = executionsPathFragment in queryParams;

    const projectsById = useSelector(getProjectsById);
    const bundlesById = useSelector(getBundlesById);
    const workspaceData = useSelector(getWorkspaceData);

    if (!isExecutionFilterPage) {
        return {
            matchExecutionFilterPage: null,
            executionFilterLocation: null as string | null,
            executionFilterLocationId: null as string | null,
        };
    }

    const recordTypeName = pathMatch?.params?.recordTypeName ?? null;

    // Prefer ?id=, otherwise resolve ?name= → ID from Redux so callers always get a UUID.
    let recordId = (queryParams.id as string | undefined) ?? null;
    if (!recordId) {
        const name = (queryParams.name as string | undefined) ?? null;
        if (name && recordTypeName === "project") {
            recordId =
                (Object.values(projectsById) as any[]).find((p) => p.name === name)?.id ?? null;
        } else if (name && recordTypeName === "bundle") {
            recordId =
                (Object.values(bundlesById) as any[]).find((b) => b.name === name)?.id ?? null;
        } else if (name && recordTypeName === "workspace") {
            recordId = (workspaceData as any)?.name === name ? (workspaceData as any)?.id : null;
        } else if (recordTypeName === "workspace") {
            recordId = (workspaceData as any)?.id ?? null;
        }
    }

    return {
        matchExecutionFilterPage: pathMatch,
        executionFilterLocation: recordTypeName,
        executionFilterLocationId: recordId,
    };
};

export const mapScreensToPaths = {
    advancedSearch: getWorkspaceLink("", advancedSearchPathFragment),
    userRoleManagement: getWorkspaceLink("", usersRolesPathFragment),
    trash: trashPath,
    executionFilter: ({
        id,
        params,
        containerType,
    }: {
        id: string;
        params?: string;
        containerType?: FeConfigType;
    }) => {
        const base =
            containerType === "project" || containerType === "bundle"
                ? executionFilterPerLocationLink(containerType, id)
                : `${getWorkspaceUrl()}?${executionsPathFragment}`;
        return params ? `${base}&${params}` : base;
    },
    canvas: ({
        id,
        containerType,
        canvasType,
    }: {
        id: string;
        containerType?: FeConfigType;
        canvasType?: CanvasType;
    }) => getCanvasLink(containerType as FeConfigType, id as string, canvasType),
    workspaceSettings: (id: string) => getWorkspaceLink(id, settingsPathFragment),
    containerLogs: (
        type: FeConfigType,
        id: string,
        params?: string,
        by: FeByType = "id"
    ) => {
        const base =
            type === "workspace"
                ? `${getWorkspaceUrl()}?${logSubPageKey}`
                : `${getRelativeUrl(type, id, by)}&${logSubPageKey}`;
        return params ? `${base}&${params}` : base;
    },
    bundleManager: (id: string) => getWorkspaceLink(id, bundleManagerPathFragment),
    storageAnalysis: (id: string) => getWorkspaceLink(id, storageAnalysisSubPageKey),
    userProfile: (id: string) => getRelativeUrl("user", id),
};

export const getWorkspaceLogsLink = (id: string, params?: string) =>
    mapScreensToPaths.containerLogs("workspace", id, params);

interface AiScreenProps {
    id: string;
    userId?: string;
    params?: string;
    containerType?: FeConfigType;
    canvasType?: CanvasType;
    screen: string;
}

export const getScreenLinks = ({ id, containerType, userId, screen }: AiScreenProps) => {
    switch (screen) {
        case "workspace_logs":
            return getWorkspaceLogsLink(id as string);
        case "execution_live_monitor":
            return mapScreensToPaths.executionFilter({ id, containerType });
        case "workspace_settings":
            return mapScreensToPaths.workspaceSettings(id);
        case "bundle_manager":
            return mapScreensToPaths.bundleManager(id);
        case "users_and_roles":
            return mapScreensToPaths.userRoleManagement;
        case "user_profile":
            return mapScreensToPaths.userProfile(userId as string);
        case "advanced_search":
            return mapScreensToPaths.advancedSearch;
        case "trash":
            return mapScreensToPaths.trash;
        case "object_template_canvas":
            return mapScreensToPaths.canvas({ id, containerType, canvasType: "template" });
        case "custom_object_canvas":
            return mapScreensToPaths.canvas({ id, containerType, canvasType: "object" });
        default:
            return "";
    }
};
